import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AppTranslationPipe } from '@adapter/i18n/app-translation.pipe';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    AppTranslationPipe,
    CardModule,
    ButtonModule,
    DividerModule
  ],
  template: `
    <div class="space-y-8">
      <!-- Header Principal -->
      <div
        class="text-center py-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl"
      >
        <h1 class="text-4xl font-bold text-gray-800 mb-4">
          {{ 'home.welcome' | translate }}
        </h1>
        <p class="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          {{ 'home.description' | translate }}
        </p>
      </div>

      <!-- Resumen General -->
      <p-card
        header="{{ 'home.overview.title' | translate }}"
        styleClass="shadow-lg"
      >
        <p class="text-gray-700 mb-6 leading-relaxed">
          {{ 'home.overview.description' | translate }}
        </p>

        <div class="grid md:grid-cols-2 gap-4">
          <div
            *ngFor="let feature of getFeatures()"
            class="flex items-start space-x-3"
          >
            <div
              class="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"
            ></div>
            <span class="text-gray-700">{{ feature }}</span>
          </div>
        </div>
      </p-card>

      <!-- Arquitectura -->
      <p-card
        header="{{ 'home.architecture.title' | translate }}"
        styleClass="shadow-lg"
      >
        <p class="text-gray-700 mb-6">
          {{ 'home.architecture.description' | translate }}
        </p>

        <div class="grid md:grid-cols-2 gap-6">
          <div
            *ngFor="let layer of getLayers()"
            class="bg-gray-50 p-4 rounded-lg"
          >
            <h4 class="font-semibold text-gray-800 mb-2">
              {{ layer.split(':')[0] }}
            </h4>
            <p class="text-gray-600 text-sm">{{ layer.split(':')[1] }}</p>
          </div>
        </div>
      </p-card>

      <!-- Diseño Atómico -->
      <p-card
        header="{{ 'home.design.title' | translate }}"
        styleClass="shadow-lg"
      >
        <p class="text-gray-700 mb-6">
          {{ 'home.design.description' | translate }}
        </p>

        <div class="space-y-4">
          <div
            *ngFor="let level of getDesignLevels()"
            class="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg"
          >
            <div
              class="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center"
            >
              <span class="text-purple-600 font-semibold text-sm">{{
                level.split(':')[0].charAt(0)
              }}</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-800">
                {{ level.split(':')[0] }}
              </h4>
              <p class="text-gray-600 text-sm">{{ level.split(':')[1] }}</p>
            </div>
          </div>
        </div>
      </p-card>

      <!-- Servicios -->
      <p-card
        header="{{ 'home.services.title' | translate }}"
        styleClass="shadow-lg"
      >
        <p class="text-gray-700 mb-6">
          {{ 'home.services.description' | translate }}
        </p>

        <div class="grid md:grid-cols-2 gap-4">
          <div
            *ngFor="let service of getServices()"
            class="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500"
          >
            <h4 class="font-semibold text-blue-800 mb-1">
              {{ service.split(':')[0] }}
            </h4>
            <p class="text-blue-700 text-sm">{{ service.split(':')[1] }}</p>
          </div>
        </div>
      </p-card>

      <!-- Stack Tecnológico -->
      <p-card
        header="{{ 'home.technologies.title' | translate }}"
        styleClass="shadow-lg"
      >
        <p class="text-gray-700 mb-6">
          {{ 'home.technologies.description' | translate }}
        </p>

        <div class="grid md:grid-cols-3 gap-6">
          <!-- Frontend -->
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
            <h4 class="font-semibold text-green-800 mb-3">Frontend</h4>
            <ul class="space-y-2">
              <li
                *ngFor="let tech of getFrontendTech()"
                class="text-green-700 text-sm flex items-center"
              >
                <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                {{ tech }}
              </li>
            </ul>
          </div>

          <!-- Testing -->
          <div class="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
            <h4 class="font-semibold text-orange-800 mb-3">Testing</h4>
            <ul class="space-y-2">
              <li
                *ngFor="let tech of getTestingTech()"
                class="text-orange-700 text-sm flex items-center"
              >
                <span class="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                {{ tech }}
              </li>
            </ul>
          </div>

          <!-- Development -->
          <div class="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
            <h4 class="font-semibold text-purple-800 mb-3">Development</h4>
            <ul class="space-y-2">
              <li
                *ngFor="let tech of getDevTech()"
                class="text-purple-700 text-sm flex items-center"
              >
                <span class="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                {{ tech }}
              </li>
            </ul>
          </div>
        </div>
      </p-card>

      <!-- Beneficios -->
      <p-card
        header="{{ 'home.benefits.title' | translate }}"
        styleClass="shadow-lg"
      >
        <div class="grid md:grid-cols-2 gap-4">
          <div
            *ngFor="let benefit of getBenefits()"
            class="flex items-start space-x-3"
          >
            <div
              class="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center"
            >
              <span class="text-yellow-600 text-sm">✨</span>
            </div>
            <span class="text-gray-700">{{ benefit }}</span>
          </div>
        </div>
      </p-card>

      <!-- Comenzando -->
      <p-card
        header="{{ 'home.gettingStarted.title' | translate }}"
        styleClass="shadow-lg"
      >
        <p class="text-gray-700 mb-6">
          {{ 'home.gettingStarted.description' | translate }}
        </p>

        <div class="space-y-3">
          <div
            *ngFor="let step of getSteps(); let i = index"
            class="flex items-center space-x-4"
          >
            <div
              class="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-semibold"
            >
              {{ i + 1 }}
            </div>
            <span class="text-gray-700">{{ step }}</span>
          </div>
        </div>
      </p-card>

      <!-- Call to Action -->
      <div
        class="text-center py-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl"
      >
        <h3 class="text-2xl font-bold text-gray-800 mb-4">
          ¿Listo para comenzar?
        </h3>
        <p class="text-gray-600 mb-6 max-w-2xl mx-auto">
          Explora las diferentes secciones del handbook para conocer en detalle
          cada aspecto de la línea base.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <button
            pButton
            type="button"
            label="Ver Estructura"
            severity="primary"
            class="px-6"
            (click)="navigateToStructure()"
          >
            Ver Estructura
          </button>
          <button
            pButton
            type="button"
            label="Explorar Componentes"
            severity="secondary"
            class="px-6"
            (click)="navigateToComponents()"
          >
            Explorar Componentes
          </button>
          <button
            pButton
            type="button"
            label="Revisar Tecnologías"
            severity="success"
            class="px-6"
            (click)="navigateToTechnologies()"
          >
            Revisar Tecnologías
          </button>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class HomeComponent {
  constructor(private router: Router) {}

  navigateToStructure(): void {
    void this.router.navigate(['/handbook/structure']);
  }

  navigateToComponents(): void {
    void this.router.navigate(['/handbook/components']);
  }

  navigateToTechnologies(): void {
    void this.router.navigate(['/handbook/technologies']);
  }

  getFeatures(): string[] {
    return [
      'Arquitectura Hexagonal (Clean Architecture)',
      'Diseño Atómico (Atomic Design)',
      'Patrones de Diseño Empresariales',
      'Testing Automatizado Completo',
      'Sistema de Traducción Multiidioma',
      'Componentes Reutilizables',
      'Servicios de Infraestructura',
      'Gestión de Estado Centralizada'
    ];
  }

  getLayers(): string[] {
    return [
      'Domain: Lógica de negocio pura',
      'Application: Casos de uso y servicios',
      'Infrastructure: Adaptadores y servicios externos',
      'Presentation: Componentes y vistas'
    ];
  }

  getDesignLevels(): string[] {
    return [
      'Atoms: Componentes básicos (botones, inputs)',
      'Molecules: Combinaciones simples (formularios)',
      'Organisms: Secciones complejas (headers, sidebars)',
      'Templates: Layouts y estructuras',
      'Pages: Vistas completas'
    ];
  }

  getServices(): string[] {
    return [
      'AppNotificationService: Notificaciones toast',
      'AppDialogService: Diálogos modales dinámicos',
      'AppConfirmationService: Confirmaciones',
      'AppTranslationService: Traducción multiidioma',
      'IdentityService: Gestión de autenticación'
    ];
  }

  getFrontendTech(): string[] {
    return [
      'Angular 17+ (Standalone Components)',
      'TypeScript 5+',
      'Tailwind CSS',
      'PrimeNG (UI Components)',
      'RxJS (Programación Reactiva)'
    ];
  }

  getTestingTech(): string[] {
    return [
      'Jasmine & Karma',
      'ng-mocks (Testing Utilities)',
      'Coverage Reports'
    ];
  }

  getDevTech(): string[] {
    return [
      'ESLint (Linting)',
      'Prettier (Code Formatting)',
      'Husky (Git Hooks)',
      'Angular CLI'
    ];
  }

  getBenefits(): string[] {
    return [
      'Desarrollo más rápido con componentes reutilizables',
      'Código mantenible y escalable',
      'Testing automatizado desde el inicio',
      'Arquitectura empresarial probada',
      'Documentación completa y ejemplos'
    ];
  }

  getSteps(): string[] {
    return [
      'Explora la estructura del proyecto',
      'Revisa los lineamientos de desarrollo',
      'Examina los componentes disponibles',
      'Prueba los servicios de ejemplo',
      'Personaliza según tus necesidades'
    ];
  }
}
